import * as React from "react"; // tslint:disable-line
import { Link } from "react-router-dom";
import styled, { createGlobalStyle, css } from "styled-components";

import Icon from "./Icon";
import { DesktopShow } from "./Panel";
import { media, space } from "./common";
import { theme } from "./theme";

export interface ExtraButtonProps {
  buttontype?: "primary";
  warning?: boolean;
  size?: "large" | "small";
  visible?: boolean;
  normal?: boolean;
  autoHight?: boolean;
}
export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  ExtraButtonProps;
export const Button = styled.button<ButtonProps>`
  display: inline-block;
  padding: 0 10px;
  height: ${(props) => (props.autoHight ? "auto" : "40px")};
  line-height: ${(props) => (props.autoHight ? "inherit" : "38px")};
  border-radius: 3px;
  font-size: ${(props) => props?.theme?.fontSize?.body};
  text-decoration: none;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: background-color ease 0.3s;
  color: ${({ buttontype }) =>
    buttontype === "primary" ? theme.white : theme.buttonColor};
  border: 1px solid
    ${({ buttontype }) =>
      buttontype === "primary" ? theme.primary : theme.buttonBorder};
  background-color: ${({ buttontype }) =>
    buttontype === "primary" ? theme.primary : theme.buttonBg};
  &:hover {
    cursor: pointer;
    color: ${({ buttontype }) =>
      buttontype === "primary" ? theme.white : theme.buttonHoverColor};
    border: 1px solid
      ${({ buttontype }) =>
        buttontype === "primary" ? theme.primaryDeep : theme.buttonHoverBorder};
    background-color: ${({ buttontype }) =>
      buttontype === "primary" ? theme.primaryDeep : theme.buttonBg};
  }
  &:focus {
    outline: none;
  }

  ${({ disabled, buttontype }) =>
    disabled &&
    css`
      && {
        color: ${theme.textGray};
        border: ${buttontype === "primary"
          ? `1px solid ${theme.bgGray}`
          : `1px solid ${theme.gray}`};
        background-color: ${buttontype === "primary"
          ? theme.bgGray
          : theme.white};
        &:hover {
          cursor: not-allowed;
        }
      }
    `};

  ${(props) =>
    props.disabled === true &&
    css`
      color: ${theme.textGray};
      border-color: ${theme.bgGray};
      background-color: ${theme.bgGray};
      cursor: not-allowed;
    `};

  ${(props) =>
    props.warning &&
    css`
      color: ${theme.primary};
      border-color: ${theme.primary};
    `};

  ${(props) =>
    props.size === "large" &&
    css`
      font-size: ${theme?.fontSize?.title3};
    `};

  ${(props) =>
    props.size === "small" &&
    css`
      height: 32px;
      line-height: 30px;
      padding: 0 8px;
      font-size: ${theme?.fontSize?.caption};
    `};

  ${(props) =>
    props.visible === false &&
    css`
      visibility: hidden;
      pointer-events: none;
    `};

  ${(props) =>
    props.normal &&
    css`
      border: none;
      background-color: transparent;
    `};
`;

export interface ButtonGroupProps {
  layout?: "flex" | "grid";
  withoutTips?: boolean;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

export const ButtonGroup = (props: ButtonGroupProps) => {
  return (
    <>
      <GlobalStyle />
      <ButtonGroupStyles {...props} />
    </>
  );
};

const ButtonGroupStyles = styled.div<ButtonGroupProps>`
  display: ${(props) => props.layout};
  margin-left: -${space}px;
  margin-right: -${space}px;
  background-color: #fff;
  ${({ withoutTips }) =>
    withoutTips &&
    css`
      ${media.xs`
        grid-template-columns: 1fr 1fr 1fr;
        grid-template-rows: 1fr;
        grid-template-areas: "exit back submit"
      `};
      grid-template-areas:
        "exit exit"
        "back submit";
      ${/* sc-custom '.link-button' */ Button} {
        grid-area: back;
      }
      ${/* sc-custom '.normal-link-button' */ TextButton} {
        grid-area: exit;
        text-align: left;
      }
      ${/* sc-custom '.button' */ Button} {
        grid-area: submit;
      }
    `} ${/* sc-selector */ DesktopShow} {
    justify-self: flex-start;
    flex: 1;
    a,
    button {
      padding: 0;
      text-align: left;
    }
  }
  ${(props) =>
    props.layout === "flex" &&
    css`
      justify-content: flex-end;
    `} ${(props) =>
    props.layout === "grid" &&
    css`
      grid-template-columns: 1fr 1fr;
      grid-template-rows: 1fr;
      grid-row-gap: ${space}px;
      &.without-tips {
        grid-template-areas:
          "exit exit"
          "back submit";
        .save-exit-btn {
          grid-area: exit;
          text-align: left;
        }
        .back-btn {
          grid-area: back;
        }
        .submit-btn {
          grid-area: submit;
        }
      }
      ${media.md`
        grid-template-columns: 1fr 1fr 1fr;
        grid-template-rows: 1fr;
      `};
    `}
  /* stylelint-disable-next-line no-descending-specificity */
  button, a {
    margin: 0 ${space}px;
    min-width: 140px;
    ${media.md`
      flex: none;
    `};
    ${(props) =>
      props.layout === "flex" &&
      css`
        flex: 1;
      `};
  }
`;

export interface TextButtonProps {
  underline?: boolean;
  buttontype?: "primary";
  highlight?: boolean;
  isExternal?: boolean;
}

export const TextButton = styled(Button)<TextButtonProps>`
  border: none;
  padding: 0;
  background-color: transparent;
  color: ${(props) => (props.highlight ? theme.blue : theme.textColor)};
  &:hover {
    color: ${(props) => (props.highlight ? theme.blue : theme.textColor)};
    border: none;
    background-color: transparent;
  }
  ${({ disabled }) =>
    disabled &&
    css`
      && {
        user-select: none;
        border: none;
        color: ${theme.textGray};
        background: transparent;
        &:hover {
          color: #a5adb5;
        }
      }
    `};

  ${Icon} {
    display: inline-block;
    margin-right: 5px;
    color: ${(props) => (props.highlight ? theme.blue : theme.textColor)};
  }
  ${({ buttontype }) =>
    buttontype === "primary" &&
    css`
      font-weight: 600;
      color: ${theme.blue};
      &:hover {
        color: ${theme.blue};
      }
      ${/* sc-custom '.text-button-icon' */ Icon} {
        color: ${theme.blue};
      }
    `};
  ${({ underline }) =>
    underline &&
    css`
      text-decoration: underline;
    `};
`;

const GlobalStyle = createGlobalStyle`
 .ant-affix {
   left: 0;
   right: 0;
   &::before, &::after {
     content:'';
     position: absolute;
     top: 0;
     width: 20px;
     height: 100%;
     z-index: 0;
     background-color: #fff;
   }
   &::before {
     left: -20px;
   }
   &::after {
     right: -20px;
   }
   ${/* sc-selector */ ButtonGroupStyles}{
     padding-top: 10px;
     padding-bottom: 10px;
     box-shadow: 0 -2px 2px ${theme.gray};
   }
 }
`;

const ButtonLink = styled.a<{ disabled?: boolean; underline?: boolean }>`
  color: ${(props) => props.theme.blue};
  font-size: ${theme?.fontSize?.body};
  font-weight: 600;
  height: 32px;
  line-height: 32px;
  user-select: none;
  ${({ underline }) =>
    underline &&
    css`
      text-decoration: underline;
    `};
  &:focus {
    text-decoration: none;
  }
  i {
    vertical-align: middle;
  }
  ${({ disabled, theme }) =>
    disabled &&
    css`
      &&,
      & * {
        color: ${theme.textGray};
        pointer-events: auto;
        cursor: not-allowed;
      }
    `};
`;

export const LinkButton: React.FC<
  TextButtonProps & ButtonProps & { to?: string; dataTest?: string }
> = (props) => {
  const {
    to,
    disabled,
    className,
    underline,
    dataTest,
    onClick,
    isExternal = false,
  } = props;
  if (onClick && !to && !disabled) {
    return (
      <ButtonLink
        className={className}
        data-test={dataTest}
        underline={underline}
        onClick={(e: React.MouseEvent<any>) => {
          onClick(e);
        }}
      >
        {props.children}
      </ButtonLink>
    );
  }
  if (to && !disabled) {
    return (
      <ButtonLink
        className={className}
        to={to}
        {...(isExternal ? { href: to } : { as: Link })}
        data-test={dataTest}
        underline={underline}
        onClick={(e: React.MouseEvent<any>) => {
          if (onClick) {
            onClick(e);
          }
        }}
      >
        {props.children}
      </ButtonLink>
    );
  }
  return <TextButton {...props}>{props.children}</TextButton>;
};
